import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';

// ─────────────────────────────────────────────────────────────
// LoginPage — original ("old UI") layout: full-screen hero photo
// with a single centered frosted-glass card. The Geonomic logo sits
// at the top of that card so it reads clearly over the background.
// Login logic is unchanged.
// ─────────────────────────────────────────────────────────────

interface LoginFieldProps {
  id: string;
  label: string;
  placeholder?: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  revealable?: boolean;
}

function LoginField({ id, label, placeholder, type = 'text', value, onChange, autoComplete, revealable }: LoginFieldProps) {
  const [show, setShow] = useState(false);
  const inputType = revealable ? (show ? 'text' : 'password') : type;
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-gray-700 mb-1.5 font-medium" style={{ fontSize: 12.5 }}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-[44px] px-3.5 rounded border border-gray-300 bg-white/95 text-gray-800 outline-none transition-colors duration-150 placeholder:text-gray-400 focus:border-primary-500"
          style={{ fontSize: 14.5, paddingRight: revealable ? 40 : undefined }}
        />
        {revealable && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
            title={show ? 'Hide password' : 'Show password'}
            tabIndex={-1}
            className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-gray-400 transition-colors hover:text-gray-600"
          >
            {show ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        )}
      </div>
    </div>
  );
}

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const login = useAuthStore((s) => s.login);
  const isLoading = useAuthStore((s) => s.isLoading);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login({ username, password });
      const user = useAuthStore.getState().user;
      navigate(user?.role === 'SuperAdmin' ? '/dashboard' : '/dashboard/wells');
    } catch {
      setError('Username or password is incorrect');
    }
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      {/* Full-screen hero photo */}
      <img
        src="/geonomic-technologies-inc-bg.jpg"
        alt="Geonomic well site"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />

      {/* Centered frosted-glass login card */}
      <div className="relative z-10 flex h-full items-center justify-center px-5">
        <div
          className="w-full max-w-[380px]"
          style={{
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            background: 'rgba(255,255,255,0.55)',
            border: '1px solid rgba(255,255,255,0.55)',
            borderRadius: 16,
            boxShadow: '0 20px 60px rgba(8,15,25,0.35)',
            padding: '30px 32px 32px',
          }}
        >
          {/* Logo */}
          <div className="mb-6 flex justify-center">
            <img
              src="/geonomic-technologies-logo.png"
              alt="Geonomic Technologies"
              className="block w-auto"
              style={{ height: 42 }}
            />
          </div>

          <h1 className="text-center font-semibold text-gray-900 mb-1" style={{ fontSize: 26, letterSpacing: -0.4 }}>
            Sign In
          </h1>
          <p className="text-center text-gray-600 mb-6" style={{ fontSize: 13.5 }}>Sign in to your account</p>

          {/* Error banner */}
          {error && (
            <div
              role="alert"
              className="mb-4 rounded border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <LoginField
              id="username"
              label="Username"
              placeholder="Enter your username"
              autoComplete="username"
              value={username}
              onChange={setUsername}
            />
            <LoginField
              id="password"
              label="Password"
              placeholder="Enter your password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              revealable
            />

            <button
              type="submit"
              disabled={isLoading}
              className="mt-2 w-full rounded bg-primary-600 py-3 font-semibold text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-85"
              style={{ fontSize: 14.5 }}
            >
              {isLoading ? 'Signing in…' : 'Login'}
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div
        className="absolute bottom-5 left-0 right-0 z-10 text-center text-white/85"
        style={{ fontSize: 11, textShadow: '0 1px 3px rgba(0,0,0,0.45)' }}
      >
        © {new Date().getFullYear()} All Rights Reserved. A product of Geonomic Technologies Inc.
      </div>
    </div>
  );
}
